import React, { useState } from 'react';
import type { Message, ReactionType } from '../types';
import { cn } from '@core/lib/utils';
import { Clock, CheckCircle, XCircle, Copy, Check, RotateCcw, Brain, ChevronDown, ChevronUp, FileText, Image as ImageIcon, Bookmark } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MessageReactions } from './MessageReactions';
import { chatConfig, useChatCopy } from '../../config';
import { messageText } from '../utils/message-text';

/**
 * #681: 'bubble' is today’s rendering and stays the default for every
 * consumer. 'prose' is the redesigned reading model, passed only from
 * pages/chat.tsx until it is validated there.
 */
export type ChatSurface = 'bubble' | 'prose';

interface ChatBubbleProps {
  message: Message;
  isNew?: boolean;
  className?: string;
  coachName?: string;
  /**
   * #1308 (CP-29/§32.1): present ONLY on the newest completed agent reply —
   * passing it on any other message is a caller bug (regenerating an older
   * turn would fork the conversation). Gated by `capabilities.actions.retry`.
   */
  onRetry?: () => void;
  onReact?: (messageId: string, reactionType: ReactionType) => void;
  /** #94 client-flag gesture: flag this AI message as an insight candidate. */
  onFlag?: (messageId: string, content: string) => Promise<void> | void;
  messageIndex?: number;
  surface?: ChatSurface;
  /**
   * TRF delta 2026-09-07: label this turn with its speaker (prose surface
   * only). MessageList sets it on the FIRST turn of each same-speaker run in a
   * VOICE transcript and nowhere else — see the comment at the render site
   * below for why voice is the one place the tint is not enough.
   */
  speakerMark?: boolean;
}

/**
 * TRF delta 2026-09-07: "9:49 PM" for the hover row. Returns '' for a
 * timestamp that will not parse, so an unparseable Date renders no stamp
 * rather than throwing out of Intl — the same rule the day divider follows.
 */
function timeOfDay(ts: Message['timestamp'], locale: string): string {
  const d = typeof ts === 'number' ? new Date(ts) : ts;
  if (Number.isNaN(d.getTime())) return '';
  // #2109 R3-B24: the copy pack's locale, never the browser's.
  return d.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
}

/** ISO form of the same instant, or undefined when it will not parse. */
function isoOf(ts: Message['timestamp']): string | undefined {
  const d = typeof ts === 'number' ? new Date(ts) : ts;
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/** Strip internal [STATE:{...}] blocks that should never be shown to users */
function stripStateBlocks(content: string): string {
  return content.replace(/\[STATE:\{[^}]*\}]/g, '').trim();
}

const MessageStatus = React.memo(function MessageStatus({
  status,
  sender
}: {
  status?: Message['status'];
  sender: Message['sender'];
}) {
  const statusCopy = useChatCopy().bubbleChrome.status;
  if (sender !== 'user' || !status) return null;

  const getStatusIcon = () => {
    switch (status) {
      case 'sending':
        return <Clock className="h-3 w-3 opacity-70" style={{ color: 'var(--stevens-ink-2)' }} />;
      case 'sent':
      case 'delivered':
        return <CheckCircle className="h-3 w-3 text-chat-status-success" />;
      case 'error':
        return <XCircle className="h-3 w-3 text-destructive" />;
      default:
        return null;
    }
  };

  return (
    <div className="flex items-center ml-1" title={statusCopy[status]}>
      {getStatusIcon()}
    </div>
  );
});

export const ChatBubble = React.memo(function ChatBubble({
  message,
  isNew = false,
  className,
  coachName,
  onRetry,
  onReact,
  onFlag,
  surface = 'bubble',
  speakerMark = false,
}: ChatBubbleProps) {
  // #681: every visual-grammar change in this component hangs off this flag.
  // `bubble` is the pre-wave rendering and must stay byte-identical for
  // CristinaChat, WorkspaceChat and NotesWorkspacePage (spec Scope table, D6).
  const isProse = surface === 'prose';
  // #2109 R3-B24: subscribing here is what re-renders a memoised bubble on a
  // language switch (the comparator below ignores the pack).
  const copy = useChatCopy();
  const chrome = copy.bubbleChrome;
  // R3-B15: a client-built system line renders in the reader's language.
  const text = messageText(message);
  const isUser = message.sender === 'user';
  const isStevens = message.sender === 'stevens';
  const isCristina = message.sender === 'cristina';
  const [copied, setCopied] = useState(false);
  const [flagState, setFlagState] = useState<'idle' | 'flagged' | 'failed'>('idle');
  const [thinkingExpanded, setThinkingExpanded] = useState(true);
  const [showActions, setShowActions] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy message:', err);
    }
  };

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    }
  };


  const handleReaction = (messageId: string, reactionType: ReactionType) => {
    if (onReact) {
      onReact(messageId, reactionType);
    }
  };

  // LMC bubble styles
  const aiBubbleStyle: React.CSSProperties = {
    background: 'var(--stevens-companion-bg)',
    color: 'var(--stevens-ink)',
    border: '1px solid var(--stevens-companion-border)',
  };

  // #1393: on the prose surface the client’s turn is a quiet grouping cue â
  // page ink on a tint one step off the page (--chat-user-* â the brand’s
  // --lmc-chat-user-* pair), NOT the accent fill. The accent fill made the
  // user’s own words the loudest thing on screen in both themes, pulling
  // the eye away from the coach’s reply. The bubble surface keeps the
  // accent verbatim: its three consumers are pinned byte-identical.
  const userBubbleStyle: React.CSSProperties = isProse
    ? {
        background: 'var(--chat-user-bg)',
        color: 'var(--chat-user-text)',
      }
    : {
        background: 'var(--stevens-indigo)',
        color: 'var(--stevens-indigo-solid-fg)',
      };

  const actionBtnStyle: React.CSSProperties = {
    background: 'var(--stevens-companion-bg)',
    border: '1px solid var(--stevens-companion-border)',
    color: 'var(--stevens-ink-2)',
  };

  // #1302 Guard B: sender display names are brand copy — they arrive via the
  // BrandChatConfig copy pack (LMC values identical to the pre-extraction
  // literals). `isStevens`/`isCristina` stay: they gate on the wire-protocol
  // sender values, which are protocol identifiers, not display copy.
  const bubbleCopy = copy.bubble;
  const senderName = isUser
    ? bubbleCopy.userLabel
    : coachName ||
      ((isStevens && bubbleCopy.senderNames['stevens']) ||
        (isCristina && bubbleCopy.senderNames['cristina']) ||
        bubbleCopy.senderFallback);

  return (
    <div
      className={cn(
        'flex w-full',
        isUser ? 'justify-end' : 'justify-start',
        isNew && 'animate-in slide-in-from-bottom-2 duration-300',
        className
      )}

      role="article"
      aria-label={isUser ? chrome.fromUser : chrome.from(senderName)}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <div className={cn(
        'flex flex-col',
        isUser ? 'items-end' : 'items-start',
        // TRF #1803 (2): the 85% cap belongs to THIS column, not to the bubble
        // inside it. A percentage max-width resolves against the containing
        // block — and this column is a shrink-to-fit flex item, so its width
        // is derived from the bubble's own max-content. Capping the BUBBLE at
        // 85% therefore capped it at 85% of ITSELF, and `break-words` then
        // split the overflow mid-word: a one-word turn ("Great.") rendered as
        // "Gre / at." in a ~90px bubble. Here the 85% resolves against the
        // full-width row, and the bubble — a flex item of an `items-*` column,
        // so already shrink-to-fit — fits its content up to that cap.
        // Prose surface only: the bubble path caps with absolute `max-w-xs/
        // md/lg` rungs, which never had the percentage problem, and
        // ChatBubble.surface.test.tsx pins its DOM byte-identical.
        isProse && isUser && 'max-w-[var(--chat-bubble-max)]'
      )}>
        {/* Label row — subtle, secondary to content */}
        {/* The trailing rhythm belongs to the ROW, not to the name span: the
            span is a flex item of an `items-center` row, so a marginBottom on
            it grows the cross size and lifts the name off the sibling <time>
            and MessageStatus. */}
        {/* Chat UX brief 2026-09-07, change #2: the prose surface drops the
            WHOLE label row — no speaker name on either side, no per-message
            stamp. Bubble alignment and tint are the only speaker cue, and the
            transcript's clock moves to the once-per-day divider MessageList
            renders above the first turn of each calendar day. (#1393 had
            already dropped the client's own "You" for the same reason; this
            finishes the job on both sides.)

            That deletion is also the row-height fix the brief asks for in the
            same change. A user row's wrapper measured 740 × 99 on the live
            page while its content needs ~71: this row is 20px of caption
            leading plus the 8px --chat-heading-trail under it, i.e. exactly
            the missing 28. Nothing on the path sets a min-height or a fixed
            row height — the dead band WAS the label row, so removing it leaves
            the wrapper at its content's height, with the action row's `mt-1.5`
            as the one small gap inside it.

            Delivery state is not decoration and does not go: prose still shows
            a user turn that is in flight or failed, and shows nothing once it
            is sent (a check mark on every delivered message is exactly the
            per-turn noise the brief is removing). The bubble path keeps this
            row byte-identical — ChatBubble.surface.test.tsx pins it for the
            three cpv3 consumers. */}
        {!isProse ? (
          <div className="flex items-center gap-1.5 mb-1">
            {!isUser && coachName ? (
              <span
                className="font-hand"
                style={{
                  color: 'var(--stevens-ink-2)',
                  fontSize: 'calc(16rem / 17 * var(--content-font-scale))',
                }}
              >
                {coachName}
              </span>
            ) : (
              <span
                className="text-caption font-medium"
                style={{
                  color: 'var(--stevens-ink-2)',
                  fontSize: 'calc(11rem / 17 * var(--content-font-scale))',
                }}
              >
                {senderName}
              </span>
            )}
            <time
              className="text-caption"
              style={{
                color: 'var(--stevens-ink-2)',
                fontSize: 'calc(11rem / 17 * var(--content-font-scale))',
                opacity: 0.7,
              }}
              // TRF (#1731): an unparseable host timestamp renders no stamp rather
              // than throwing from toISOString()/Intl (upstream assumed a valid Date).
              dateTime={(() => {
                const d = typeof message.timestamp === 'number' ? new Date(message.timestamp) : message.timestamp;
                return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
              })()}
              suppressHydrationWarning={true}
            >
              {(() => {
                const date = typeof message.timestamp === 'number' ? new Date(message.timestamp) : message.timestamp;
                if (Number.isNaN(date.getTime())) return '';
                const dateStr = copy.time.date(date, { month: '2-digit', day: '2-digit', year: '2-digit' });
                const timeStr = date.toLocaleTimeString(copy.locale, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                return `${dateStr} ${timeStr}`;
              })()}
            </time>
            <MessageStatus status={message.status} sender={message.sender} />
          </div>
        ) : isUser && (message.status === 'sending' || message.status === 'error') ? (
          <div className="flex items-center mb-[var(--chat-heading-trail)]">
            <MessageStatus status={message.status} sender={message.sender} />
          </div>
        ) : null}

        {/* TRF delta 2026-09-07 (adversarial review, finding 4): the ONE
            place the prose surface still names a speaker.

            The chat UX brief deleted the per-turn label row on purpose, and
            for a typed thread the deletion holds: a user turn is a tinted,
            right-aligned box and an agent turn is left-aligned markdown, so
            alignment and shape carry the attribution on their own. A VOICE
            transcript has neither of those cues. Both sides arrive as
            unstructured speech with no markdown, no lists and no headings, and
            the tint between them is one paper step — ~2.5% — so a long call
            reads as one undifferentiated column of prose.

            So voice, and only voice, gets a name back — once per RUN, not once
            per turn. MessageList decides which turn starts a run; a five-turn
            answer is labelled at its top and nowhere else, which is the
            grammar the day divider already uses for dates. */}
        {isProse && speakerMark && (
          <span
            className="text-caption font-medium mb-1"
            data-testid="speaker-mark"
            style={{
              color: 'var(--stevens-ink-2)',
              fontSize: 'calc(11rem / 17 * var(--content-font-scale))',
            }}
          >
            {senderName}
          </span>
        )}

        {/* Extended thinking display (collapsible) */}
        {message.thinkingContent && !isUser && (
          <button
            onClick={() => setThinkingExpanded(!thinkingExpanded)}
            className={cn(
              'flex items-center gap-2 px-3 py-1.5 mb-2 rounded-md text-caption',
              'transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-primary/20'
            )}
            style={{
              background: 'color-mix(in srgb, var(--stevens-companion-bg) 60%, transparent)',
              border: '1px solid color-mix(in srgb, var(--stevens-companion-border) 50%, transparent)',
              color: 'var(--stevens-ink-2)',
            }}
            aria-expanded={thinkingExpanded}
            aria-label={chrome.thinkingToggle}
          >
            <Brain className="h-3 w-3" />
            <span className="font-normal">{chrome.thinkingLabel}</span>
            {thinkingExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
        )}

        {message.thinkingContent && !isUser && thinkingExpanded && (
          <div
            className={cn(
              'rounded-lg px-3 py-2 mb-2 max-w-xs md:max-w-md lg:max-w-lg break-words'
            )}
            style={{
              background: 'color-mix(in srgb, var(--stevens-companion-bg) 40%, transparent)',
              border: '1px solid color-mix(in srgb, var(--stevens-companion-border) 30%, transparent)',
            }}
          >
            <div
              className="text-body-sm leading-relaxed whitespace-pre-wrap font-light"
              style={{ color: 'color-mix(in srgb, var(--stevens-ink-2) 90%, transparent)' }}
            >
              {message.thinkingContent}
            </div>
          </div>
        )}

        {/* Message content — visually dominant */}
        <div className="relative group">
          <div
            className={cn(
              // #666: `prose`/`prose-*` classes below require
              // @tailwindcss/typography, which is NOT installed in this repo
              // (see frontend/tailwind.config.js `plugins: []`) — they were
              // silent no-ops, which is why assistant messages rendered as a
              // dense wall of text everywhere (main chat AND workspace
              // threads share this exact component). Replaced with explicit
              // arbitrary-variant child selectors that don’t depend on the
              // missing plugin, so both consumers get real paragraph/list/
              // heading spacing.
              //
              // The two surfaces are written as two complete, disjoint class
              // sets rather than one set with prose overrides layered on. That
              // is deliberate: the whole visual grammar (#681/#682/#683) is
              // gated to /chat, and an override-style list leaks the moment a
              // new rule forgets its `isProse &&`.
              isProse
                ? [
                    'break-words',
                    // #681: assistant messages lose their container entirely.
                    // --stevens-companion-bg measures 1.10:1 against the page in
                    // dark and 1.02:1 in light — invisible in both, while
                    // costing 96px of rail and 32px of horizontal padding.
                    !isUser && 'w-full max-w-none p-0',
                    // #682: lg:max-w-lg reads as 512px and renders 544px.
                    // Nobody chose 544. TRF #1803 (2): the cap moved UP to the
                    // column — see the note there. `max-w-full` keeps the
                    // bubble inside that column and nothing more.
                    isUser && 'max-w-full',
                    '[&_p]:my-[calc(var(--chat-para-gap)/2)] [&_p:first-child]:mt-0 [&_p:last-child]:mb-0',
                    '[&_ul]:my-[var(--chat-para-gap)] [&_ol]:my-[var(--chat-para-gap)] [&_li]:my-[calc(var(--chat-list-gap)/2)] [&_ul]:pl-5 [&_ol]:pl-5 [&_li]:leading-relaxed [&_ul]:list-disc [&_ol]:list-decimal',
                    '[&_h1]:mt-[var(--chat-heading-lead)] [&_h1]:mb-[var(--chat-heading-trail)] [&_h2]:mt-[var(--chat-heading-lead)] [&_h2]:mb-[var(--chat-heading-trail)] [&_h3]:mt-[var(--chat-heading-lead)] [&_h3]:mb-[var(--chat-heading-trail)] [&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold [&_h2]:text-[length:calc(var(--chat-heading-size)*var(--content-font-scale))]',
                    '[&_strong]:font-semibold [&_a]:underline [&_blockquote]:my-[var(--chat-para-gap)] [&_blockquote]:pl-4 [&_blockquote]:border-l-2 [&_blockquote]:border-current [&_blockquote]:opacity-90',
                    '[&_code]:rounded [&_code]:px-1 [&_code]:py-0.5 [&_code]:bg-chat-scrim [&_code]:text-[length:calc(var(--chat-code-size)*var(--content-font-scale))]',
                  ]
                : // Pre-wave literals, verbatim from fe6688462. Do not
                  // "improve" these — ChatBubble.surface.test.tsx pins them.
                  [
                    'rounded-lg px-4 py-2.5 max-w-xs md:max-w-md lg:max-w-lg break-words',
                    '[&_p]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0',
                    '[&_ul]:my-2 [&_ol]:my-2 [&_li]:my-1 [&_ul]:pl-5 [&_ol]:pl-5 [&_li]:leading-relaxed [&_ul]:list-disc [&_ol]:list-decimal',
                    '[&_h1]:mt-3 [&_h1]:mb-2 [&_h2]:mt-3 [&_h2]:mb-2 [&_h3]:mt-3 [&_h3]:mb-2 [&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold',
                    '[&_strong]:font-semibold [&_a]:underline [&_blockquote]:my-2 [&_blockquote]:pl-3 [&_blockquote]:border-l-2 [&_blockquote]:border-current [&_blockquote]:opacity-90',
                    '[&_code]:rounded [&_code]:px-1 [&_code]:py-0.5 [&_code]:bg-chat-scrim',
                  ],
            )}
            style={
              isProse
                ? {
                    ...(isUser
                      ? {
                          ...userBubbleStyle,
                          borderRadius: 'var(--chat-bubble-radius)',
                          padding: 'var(--chat-bubble-pad-v) var(--chat-bubble-pad-h)',
                        }
                      : {}), // no background, no border, no padding
                    // Backlog 2026-07-13 (a11y font-size toggle). The comment
                    // here used to read "--text-body already embeds
                    // --content-font-scale, so the Aa control keeps working."
                    // That was true of cpv3's token and FALSE of TRF's: this
                    // surface reads `--chat-body-size`, a bare 14px, and an
                    // inline style outranks every stylesheet rule — so the one
                    // surface that OFFERS the Aa control was the one surface it
                    // could not resize. (The bubble branch below has always
                    // scaled, which is how the claim survived review.)
                    // Found on production 2026-09-07 after the `.chat-prose`
                    // fix landed: `/chat` carries no `.chat-prose` element.
                    fontSize: 'calc(var(--chat-body-size) * var(--content-font-scale))',
                    lineHeight: 'var(--chat-body-lh)',
                    letterSpacing: 'var(--chat-body-ls)',
                  }
                : {
                    ...(isUser ? userBubbleStyle : aiBubbleStyle),
                    // Pre-wave literals. 14px body, 1.6 leading, NO letter
                    // spacing — the 17px/1.471/-0.022em grammar is /chat-only.
                    fontSize: 'calc(14rem / 17 * var(--content-font-scale))',
                    lineHeight: 1.6,
                  }
            }
          >
            {message.attachment && (
              <div className="mb-1.5 inline-flex items-center gap-1.5 rounded-md bg-chat-scrim px-2 py-1 text-caption">
                {message.attachment.contentType === 'application/pdf' ||
                message.attachment.contentType.startsWith('text/') ? (
                  <FileText className="h-3.5 w-3.5" />
                ) : (
                  <ImageIcon className="h-3.5 w-3.5" />
                )}
                {/* #215: history rehydration has no original filename (only the
                    S3 key persists) — fall back to a generic label by type. */}
                <span className="max-w-[200px] truncate">
                  {message.attachment.filename ??
                    (message.attachment.contentType === 'application/pdf'
                      ? chrome.attachment.pdf
                      : message.attachment.contentType.startsWith('text/')
                        ? chrome.attachment.text
                        : chrome.attachment.image)}
                </span>
              </div>
            )}

            {!isUser ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {stripStateBlocks(text)}
              </ReactMarkdown>
            ) : (
              text
            )}

            {/* #1393: the ms readout is an engineering trace, not something a
                client needs under a coaching reply — prose hides it. */}
            {message.metadata?.processingTime && !isUser && !isProse && (
              <div className="text-caption opacity-50 mt-1">
                {/* #2109 R3-B24: the unit is Intl's, in the pack locale ("1234ms" in en). */}
                {new Intl.NumberFormat(copy.locale, {
                  style: 'unit',
                  unit: 'millisecond',
                  unitDisplay: 'narrow',
                  useGrouping: false,
                }).format(message.metadata.processingTime)}
              </div>
            )}
          </div>

          {/* Action buttons - appear on hover.
              #1393: on the prose surface the assistant turn has no container,
              so a row floating at its top-right corner sat ON the first line
              of text. Prose puts the row under the message, aligned with the
              speaker (left for the coach, right for the client) — the model
              claude.ai uses. Bubble keeps the corner overlay. */}
          {/* TRF delta 2026-09-07: on the prose surface this row is taken OUT
              of flow. `opacity-0` hides paint, never layout, so the row was
              reserving ~30px under EVERY message — 6px margin, a 12px icon in
              a `p-1.5` button — which is about 1,350px of invisible height on a
              45-turn transcript and the real cause of the "huge spaces"
              Victor saw. Absolute inside a zero-height relative strip paints it
              INTO the gap on hover instead, which keeps the no-layout-shift
              behaviour a conditional render would break. */}
          <div className={cn('relative', isProse && 'h-0')}>
          <div className={cn(
            isProse
              ? cn('absolute top-0.5 flex gap-1 transition-opacity', isUser ? 'right-0 justify-end' : 'left-0 justify-start')
              : 'absolute -top-2 -right-2 flex gap-1 transition-opacity',
            showActions ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus-within:opacity-100'
          )}>
            {chatConfig().capabilities.actions.copy && (
              <button
                onClick={handleCopy}
                className={cn(
                  'p-1.5 rounded-md shadow-sm',
                  'focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring'
                )}
                style={actionBtnStyle}
                title={copied ? copy.actions.copied : copy.actions.copyMessage}
                aria-label={copied ? copy.actions.copied : copy.actions.copyMessage}
              >
                {copied ? (
                  <Check className="h-3 w-3 text-chat-status-success" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
              </button>
            )}


            {!isUser && onRetry && chatConfig().capabilities.actions.retry && (
              <button
                onClick={handleRetry}
                className={cn(
                  'p-1.5 rounded-md shadow-sm',
                  'focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring'
                )}
                style={actionBtnStyle}
                title={copy.actions.retryMessage}
                aria-label={copy.actions.retryMessage}
              >
                <RotateCcw className="h-3 w-3" />
              </button>
            )}

            {!isUser && onFlag && (
              <button
                onClick={async () => {
                  try {
                    await onFlag(message.id, stripStateBlocks(text));
                    setFlagState('flagged');
                  } catch {
                    // Never show fake success — the flag was not persisted.
                    setFlagState('failed');
                  }
                  setTimeout(() => setFlagState('idle'), 2500);
                }}
                className={cn(
                  'p-1.5 rounded-md shadow-sm',
                  'focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring'
                )}
                style={actionBtnStyle}
                title={
                  flagState === 'flagged'
                    ? chrome.flag.flagged
                    : flagState === 'failed'
                      ? chrome.flag.failed
                      : chrome.flag.idle
                }
                aria-label={chrome.flag.ariaLabel}
              >
                {flagState === 'flagged' ? (
                  <Check className="h-3 w-3 text-chat-status-success" />
                ) : flagState === 'failed' ? (
                  <XCircle className="h-3 w-3 text-destructive" />
                ) : (
                  <Bookmark className="h-3 w-3" />
                )}
              </button>
            )}

            {/* TRF delta 2026-09-07 (adversarial review, finding 3): the time
                of day, which the brief's deletions had left NOWHERE in the
                document — the day divider gives a date and nothing else, so a
                transcript could not answer "when did I say that". It rides the
                hover row rather than coming back as a per-turn label because
                this row costs no layout: it is absolutely positioned inside a
                zero-height strip, so adding to it adds 0px to every message.
                Hover and keyboard focus are the same reveal the copy/retry/flag
                controls already use, and `<time datetime>` keeps the machine
                -readable instant on the page even while the row is transparent. */}
            {isProse && timeOfDay(message.timestamp, copy.locale) && (
              <time
                className="self-center px-1 tabular-nums"
                data-testid="turn-time"
                style={{
                  color: 'var(--stevens-ink-2)',
                  fontSize: 'calc(11rem / 17 * var(--content-font-scale))',
                }}
                dateTime={isoOf(message.timestamp)}
                suppressHydrationWarning={true}
              >
                {timeOfDay(message.timestamp, copy.locale)}
              </time>
            )}
          </div>
          </div>
        </div>

        {/* Message Reactions — feedback deferred per O-12 (settled
            2026-08-31): renders only when the brand’s actions.feedback flag
            is on; no current brand enables it. */}
        {!isUser && onReact && chatConfig().capabilities.actions.feedback && (
          <div className={cn(
            'mt-2 transition-opacity',
            showActions ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          )}>
            <MessageReactions
              messageId={message.id}
              reactions={message.reactions}
              onReact={handleReaction}
              disabled={message.status === 'sending'}
            />
          </div>
        )}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.message.id === nextProps.message.id &&
    prevProps.message.content === nextProps.message.content &&
    prevProps.message.kind === nextProps.message.kind &&
    prevProps.message.params === nextProps.message.params &&
    prevProps.message.status === nextProps.message.status &&
    prevProps.message.isStreaming === nextProps.message.isStreaming &&
    prevProps.message.attachment?.attachmentRef === nextProps.message.attachment?.attachmentRef &&
    prevProps.isNew === nextProps.isNew &&
    prevProps.coachName === nextProps.coachName &&
    // #1308: retry is newest-reply-only, so onRetry appears/disappears as
    // newer replies land — compare PRESENCE (identity churns per render and
    // would defeat the memo entirely).
    (prevProps.onRetry === undefined) === (nextProps.onRetry === undefined)
  );
});
